import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Phone, Lock, Heart, Headphones, BookOpen, CalendarClock, Tag } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import AudioAvatarPlayer from './AudioAvatarPlayer';
import Avatar from './Avatar';
import CallCreatorSection from './o2ocall/CallCreatorSection';
import { langLabel } from '../lib/creatorMeta';
import { useCategories } from '../api/categories';
import { toggleFollow } from '../api/social';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { PersonScheduledLive } from './PersonScheduledLive';
import { useCallAvailability } from '../hooks/useCallAvailability';
import { CallAvailabilityButton } from './o2ocall/CallAvailabilityButton';

const MAX_TOPICS = 3;

/**
 * Phase F1 · PersonCard — anonymous voice-first business card.
 * Public identity ONLY: @username + avatar (+ optional voice, languages,
 * topics, Voce Pro badge, live state). Never full_name, never social
 * metrics (likes/followers/posts). No video elements.
 */
const PersonCard = ({ p }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('home');
  const { user: me } = useAuth();
  const { toast } = useToast();
  const [callOpen, setCallOpen] = useState(false);
  // Phase F2 · ♡ Preferiti — backed by the EXISTING follow relation.
  const [fav, setFav] = useState(!!p.is_favorite);
  const [favBusy, setFavBusy] = useState(false);
  const isMe = me?.username === p.username;
  const callStatus = useCallAvailability(p.user_id, !!me && !isMe);
  // Phase F5.2 — macro-category name + voluntary gender label.
  const { nameOf } = useCategories();
  const categoryName = p.category_slug ? nameOf(p.category_slug) : '';
  const genderText = p.gender ? t(`people.gender_${p.gender}`) : '';

  const openProfile = () => navigate(`/u/${p.username}`);
  const stop = (e) => e.stopPropagation();

  const toggleFav = async (e) => {
    stop(e);
    if (favBusy) return;
    setFavBusy(true);
    const prev = fav;
    setFav(!prev);
    try {
      const res = await toggleFollow(p.username);
      setFav(!!res.following);
    } catch (_err) {
      setFav(prev);
      toast({ title: t('people.fav_error'), variant: 'destructive' });
    } finally {
      setFavBusy(false);
    }
  };

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={openProfile}
      onKeyDown={(e) => { if (e.target === e.currentTarget && e.key === 'Enter') openProfile(); }}
      className="cursor-pointer rounded-xl border border-border bg-card p-4 transition-colors hover:border-amber-500/40"
      data-testid={`person-card-${p.username}`}
    >
      <div className="flex items-start gap-3.5">
        <div className="relative shrink-0">
          <div className="rounded-full bg-gradient-to-tr from-amber-300 via-yellow-400 to-amber-600 p-[2px]">
            <div className="rounded-full bg-card p-[2px]">
              {/* Avatar fix — common component: clean initial fallback on broken URLs */}
              <Avatar src={p.avatar_url} name={p.username} size={64} />
            </div>
          </div>
          {p.is_live ? (
            <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 animate-pulse rounded-full border-2 border-card bg-red-600" aria-hidden="true" />
          ) : (
            <span
              className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-card ${p.is_online ? 'bg-emerald-500' : 'bg-neutral-600'}`}
              title={p.is_online ? t('people.online') : t('people.offline')}
              aria-label={p.is_online ? t('people.online') : t('people.offline')}
              data-testid={`person-presence-${p.username}-${p.is_online ? 'online' : 'offline'}`}
            />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-[15px] font-semibold" data-testid={`person-username-${p.username}`}>
              @{p.username}
            </span>
            {p.is_voce_pro && (
              <span
                className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider gold-text"
                data-testid={`person-badge-vocepro-${p.username}`}
              >
                {t('people.voce_pro')}
              </span>
            )}
            {p.is_private_profile && (
              <Lock
                className="h-3.5 w-3.5 text-muted-foreground"
                aria-label={t('people.private_profile')}
                data-testid={`person-private-${p.username}`}
              />
            )}
            {!isMe && (
              <button
                onClick={toggleFav}
                aria-label={fav ? t('people.fav_remove') : t('people.fav_add')}
                title={fav ? t('people.fav_remove') : t('people.fav_add')}
                className="ml-auto p-1"
                data-testid={`person-fav-btn-${p.username}-${fav ? 'on' : 'off'}`}
              >
                <Heart
                  className={`h-5 w-5 transition-transform active:scale-90 ${fav ? 'fill-amber-400 gold-text' : 'text-muted-foreground hover:text-foreground'}`}
                  strokeWidth={1.8}
                />
              </button>
            )}
          </div>

          {/* Phase F5.2 — "Uomo · Italiano" line (voluntary gender, no
              placeholder when missing; dynamic separators, no empty rows) */}
          {(genderText || p.languages?.length > 0 || p.is_live || p.in_call || p.has_upcoming_live) && (
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {(genderText || p.languages?.length > 0) && (
                <span className="text-xs text-muted-foreground" data-testid={`person-meta-${p.username}`}>
                  {[genderText, ...(p.languages || []).map((l) => langLabel(l))].filter(Boolean).join(' · ')}
                </span>
              )}
              {p.is_live && (
                <button
                  onClick={(e) => { stop(e); navigate(`/lives/${p.live_id}`); }}
                  className="inline-flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
                  data-testid={`person-live-badge-${p.username}`}
                >
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> {t('people.live_now')}
                </button>
              )}
              {/* Activity badges — shown ONLY for real ongoing/planned activity */}
              {!p.is_live && p.in_call && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-400"
                  data-testid={`person-incall-badge-${p.username}`}
                >
                  <Phone className="h-3 w-3" /> {t('people.in_call')}
                </span>
              )}
              {!p.is_live && !p.in_call && p.has_upcoming_live && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground"
                  data-testid={`person-scheduled-badge-${p.username}`}
                >
                  <CalendarClock className="h-3 w-3 gold-text" /> {t('people.scheduled_live')}
                </span>
              )}
            </div>
          )}

          {/* "Argomenti trattati · Categoria" — main category only (Voce Pro
              without one arrive from the backend as "generale" by default). */}
          {categoryName && (
            <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs" data-testid={`person-category-${p.username}`}>
              <Tag className="h-3 w-3 shrink-0 gold-text" strokeWidth={2} />
              <span className="text-muted-foreground">{t('people.topics_label')}</span>
              <span className="font-semibold gold-text" data-testid={`person-category-name-${p.username}`}>{categoryName}</span>
            </p>
          )}

          {/* F2.1 — short public bio, max 2 lines with ellipsis. Absent bio
              renders nothing (card stays compact). */}
          {p.bio && (
            <p
              className="mt-1.5 line-clamp-2 text-xs leading-snug text-muted-foreground"
              data-testid={`person-bio-${p.username}`}
            >
              {p.bio}
            </p>
          )}

          {p.audio_avatar_url && (
            <div className="mt-2 flex items-center gap-2" onClick={stop} data-testid={`person-audio-${p.username}`}>
              <AudioAvatarPlayer src={p.audio_avatar_url} size="sm" />
              <span className="text-xs text-muted-foreground">{t('people.listen_voice')}</span>
            </div>
          )}

          {/* Phase F9.1 — what this person OFFERS (informational, never
              social counters). Batch flags from /api/feed/people. */}
          {(p.has_audio_racconti || p.has_text_racconti) && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`person-services-${p.username}`}>
              {p.has_audio_racconti && (
                <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-muted-foreground" data-testid={`person-service-audio-${p.username}`}>
                  <Headphones className="h-3 w-3 gold-text" /> {t('people.svc_audio')}
                </span>
              )}
              {p.has_text_racconti && (
                <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-muted-foreground" data-testid={`person-service-text-${p.username}`}>
                  <BookOpen className="h-3 w-3 gold-text" /> {t('people.svc_text')}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Phase F9.1 — [☎ Chiama][💬 Messaggio]: Chiama first, Nymia gold
          (recognizable), Messaggio secondary. EXACT existing O2O flow. */}
      <PersonScheduledLive live={p.next_scheduled_live} username={p.username} />
      <div className="mt-3 flex items-center gap-2" onClick={stop}>
        {!isMe && (
          <CallAvailabilityButton
            available={callStatus?.o2o_available}
            occupied={callStatus?.o2o_busy}
            className="h-8 flex-1 rounded-full text-xs"
            onClick={(e) => { stop(e); setCallOpen(true); }}
            testId={`person-call-btn-${p.username}`}
          />
        )}
        <Button
          variant="secondary"
          size="sm"
          className="h-8 flex-1 rounded-full text-xs font-semibold"
          onClick={(e) => { stop(e); navigate(`/messages?u=${p.username}`); }}
          data-testid={`person-message-btn-${p.username}`}
        >
          <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> {t('people.message')}
        </Button>
      </div>

      {/* One-to-One: EXACT existing flow (CallCreatorSection) in a dialog */}
      <Dialog open={callOpen} onOpenChange={setCallOpen}>
        <DialogContent className="max-w-md" onClick={stop} data-testid={`person-call-dialog-${p.username}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-4 w-4 gold-text" /> @{p.username}
            </DialogTitle>
          </DialogHeader>
          <CallCreatorSection creatorId={p.user_id} username={p.username} />
        </DialogContent>
      </Dialog>
    </article>
  );
};

export default React.memo(PersonCard);
